import { useState } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarCollapsibleTrigger,
  SidebarMenuButton,
  useSidebar,
} from "@/components/blocks/evo-sidebar";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { PlayCircle } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Settings, Star } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useFavorites } from "./hooks/useFavorites";
import { useMenuPermissions } from "./hooks/useMenuPermissions";
import { DatabaseSwitcher } from "./components/DatabaseSwitcher";
import { CompanySelector } from '@/components/CompanySelector';
import { Separator } from '@/components/ui/separator';
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import type { LeftMenuProps, MenuItem } from "./types";
import { menuItems, settingsMenuItems, databases, menuConfig } from "./config/menuItems";
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';

/**
 * Left Menu Component (Modular Version)
 *
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (enabled for 22 pages)
 * - Nested settings menu
 * - Context menu for favoriting items
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const [selectedDatabase, setSelectedDatabase] = useState("production");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const { state, isMobile } = useSidebar();

  // Onboarding resume — visible to admins when not yet completed
  const { hasCompleted, openWizard } = useOnboarding();
  const { canManageRolesPermissions } = usePermissions();
  const showResumeOnboarding = canManageRolesPermissions() && !hasCompleted;

  // Filter menu items based on user permissions
  const { filteredMenuItems, filteredSettingsItems, showSettings } = useMenuPermissions(menuItems, settingsMenuItems);

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  // Get all visible items including nested children for favorites
  const getAllItems = () => {
    const allItems: MenuItem[] = [];
    filteredMenuItems.forEach((item) => {
      allItems.push(item);
      if (item.children) {
        allItems.push(...item.children);
      }
    });
    filteredSettingsItems.forEach((item) => {
      allItems.push(item);
    });
    return allItems;
  };

  const favoriteItems = getAllItems().filter((item) => isFavorite(item.id));

  // Find the active item based on the current path
  const findActiveItem = (path: string): string => {
    // Check main menu items — children first so child pages take priority over parent
    for (const item of filteredMenuItems) {
      // Check children first
      if (item.children) {
        const child = item.children.find((c) => c.page === path);
        if (child) return `main-${child.id}`;
      }
      if (item.page === path) return `main-${item.id}`;
    }

    // Check settings items
    const settingsItem = filteredSettingsItems.find((item) => item.page === path);
    if (settingsItem) return `main-${settingsItem.id}`;

    return "";
  };

  const activeItemId = findActiveItem(activePage);

  const { confirmNavigation } = useUnsavedChanges();

  const handleItemClick = (page: string, itemId: string) => {
    confirmNavigation(() => onPageChange(page));
  };

  /**
   * Handle accordion behavior for collapsible menu items
   * Only one menu can be expanded at a time
   */
  const handleMenuToggle = (menuId: string) => {
    setOpenMenuId((currentOpenId) => {
      if (currentOpenId === menuId) return null;
      return menuId;
    });
  };

  const renderMenuItem = (item: any, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="relative h-full">
      {/* Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[160px] group-data-[state=collapsed]:hidden text-neutral-700 flex-1">
            Access Time
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center">
        {/* Favorites Section */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">

              {/* Render all menu items (filtered by permissions) */}
              {filteredMenuItems.map((item) => {
                if (item.children && item.children.length > 0) {
                  const Icon = item.icon;

                  // When collapsed, show dropdown menu
                  if (state === "collapsed") {
                    return (
                      <SidebarMenuItem key={item.id} {...(item.id === 'reports' ? { 'data-tour': 'reports-nav' } : {})}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <SidebarMenuButton tooltip={item.label}>
                              <Icon />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            side={isMobile ? "bottom" : "right"}
                            align={isMobile ? "end" : "start"}
                            className="w-48"
                          >
                            {item.children.map((child) => (
                              <DropdownMenuItem
                                key={child.id}
                                onSelect={() => handleItemClick(child.page, `main-${child.id}`)}
                                className={activeItemId === `main-${child.id}` ? "bg-muted text-foreground" : ""}
                              >
                                <span>{child.label}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuItem>
                    );
                  }

                  // When expanded, show collapsible menu
                  return (
                    <Collapsible
                      key={item.id}
                      asChild
                      open={openMenuId === item.id}
                      onOpenChange={() => handleMenuToggle(item.id)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem {...(item.id === 'reports' ? { 'data-tour': 'reports-nav' } : {})}>
                        <SidebarCollapsibleTrigger tooltip={item.label}>
                          <Icon />
                          <span>{item.label}</span>
                        </SidebarCollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.children.map((child) => (
                              <SidebarMenuSubItem key={child.id}>
                                {favoritesEnabled ? (
                                  <ContextMenu>
                                    <ContextMenuTrigger asChild>
                                      <SidebarMenuSubButton
                                        onClick={() => handleItemClick(child.page, `main-${child.id}`)}
                                        isActive={activeItemId === `main-${child.id}`}
                                      >
                                        <span>{child.label}</span>
                                      </SidebarMenuSubButton>
                                    </ContextMenuTrigger>
                                    <ContextMenuContent>
                                      <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                        <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                        {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                      </ContextMenuItem>
                                    </ContextMenuContent>
                                  </ContextMenu>
                                ) : (
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick(child.page, `main-${child.id}`)}
                                    isActive={activeItemId === `main-${child.id}`}
                                  >
                                    <span>{child.label}</span>
                                  </SidebarMenuSubButton>
                                )}
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                // Otherwise, render as simple menu item
                return (
                  <SidebarMenuItem key={item.id}>
                    {renderMenuItem(item, `main-${item.id}`)}
                  </SidebarMenuItem>
                );
              })}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        {showSettings && (
          <>
            {state === "collapsed" ? (
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton tooltip="Settings">
                      <Settings />
                      <span>Settings</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side={isMobile ? "bottom" : "right"}
                    align={isMobile ? "end" : "start"}
                    className="w-48"
                  >
                    {filteredSettingsItems.map((settingsItem) => (
                      <DropdownMenuItem
                        key={settingsItem.id}
                        onSelect={() => handleItemClick(settingsItem.page, `main-${settingsItem.id}`)}
                        className={activeItemId === `main-${settingsItem.id}` ? "bg-muted text-foreground" : ""}
                      >
                        <span>{settingsItem.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            ) : (
              <Collapsible
                asChild
                open={openMenuId === "settings"}
                onOpenChange={() => handleMenuToggle("settings")}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <SidebarCollapsibleTrigger tooltip="Settings">
                    <Settings />
                    <span>Settings</span>
                  </SidebarCollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {filteredSettingsItems.map((settingsItem) => (
                        <SidebarMenuSubItem key={settingsItem.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(settingsItem.page, `main-${settingsItem.id}`)}
                                  isActive={activeItemId === `main-${settingsItem.id}`}
                                >
                                  <span>{settingsItem.label}</span>
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(settingsItem.id)}>
                                  <Star className={isFavorite(settingsItem.id) ? "fill-current" : ""} />
                                  {isFavorite(settingsItem.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(settingsItem.page, `main-${settingsItem.id}`)}
                              isActive={activeItemId === `main-${settingsItem.id}`}
                            >
                              <span>{settingsItem.label}</span>
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}
          </>
        )}

        {menuConfig.showDatabaseSwitcher && (
          <DatabaseSwitcher
            databases={databases}
            selectedDatabase={selectedDatabase}
            onDatabaseChange={setSelectedDatabase}
            label="Database"
          />
        )}
        <Separator />
        <div className="pt-2">
          <CompanySelector variant="sidebar" collapsed={state === 'collapsed'} />
        </div>

        {/* Resume Onboarding — visible to admins who haven't completed setup */}
        {showResumeOnboarding && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <SidebarMenuItem className="group-data-[state=collapsed]:hidden">
              <SidebarMenuButton
                tooltip="Resume Onboarding"
                onClick={openWizard}
                className="text-teal-700 hover:bg-teal-50"
              >
                <PlayCircle className="text-teal-600" />
                <span className="font-medium">Resume Onboarding</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
